// client/src/routes/admin/route.tsx — the /admin layout · ADM-001 (04 §8.19, §9.2.2).
//
// The third sibling of `app/route.tsx` and `portal/route.tsx`: the shell is rendered ONCE
// around every `/admin/*` screen, and the guard decides who gets to see it.
//
// ── the guard is a PERMISSION, and the only one in the area ────────────────────────────────
// `/portal` gates on the role being `tenant`; this one gates on holding `Platform.Admin`.
// Checked once, here, rather than per screen or per nav entry: the whole area is behind the one
// code, and a second check could only ever disagree with this one (D-1640).
//
// It is a permission and not `role === 'admin'` on purpose. The role is what `SessionIssuer`
// derives from `is_platform_admin` when a user holds no membership, and the permission is what
// the API actually enforces on all seven routes. Gating the SPA on the same fact the server
// gates on means the two cannot drift into a screen that renders and then 403s on every query.
//
// ── where a refused caller goes, and why it is not SCR-SYS-403 ─────────────────────────────
// An owner, manager, accountant or admin-less user who types `/admin` is sent back to the app,
// the way `/portal` sends a landlord back (04 §9.2.2). „Acces interzis" would read as a bug in
// their account: the platform area is not a screen they are forbidden from, it is not their
// product surface. A tenant is bounced by `/app`'s own guard from there, so the two redirects
// compose rather than loop.
//
// ── and where a platform admin goes when they land on /app ─────────────────────────────────
// The mirror of the above lives in `app/route.tsx`: a platform admin holds no organization
// permission, so every screen under `/app` would render SCR-SYS-403 for them. They are sent
// here instead, which is what makes `/admin` their DEFAULT_LANDING rather than a URL they have
// to know (D-1642).

import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { toProblemDetails } from '@/api/problem-details';
import { AdminShell } from '@/components/layout/AdminShell';
import { SystemState } from '@/components/ui/states';
import { DEFAULT_LANDING } from '@/features/auth/redirect';
import { ensureSession, useHasSession } from '@/session';

export const Route = createFileRoute('/admin')({
  beforeLoad: async ({ location }) => {
    const session = await ensureSession();
    if (!session) throw redirect({ to: '/login', search: { redirect: location.href } });
    if (!session.permissions.includes('Platform.Admin')) throw redirect({ href: DEFAULT_LANDING });
  },
  component: AdminLayout,
  // §8.20: both system states render INSIDE the active shell, and for anything under `/admin`
  // the active shell is this one.
  errorComponent: AdminErrorComponent,
  notFoundComponent: AdminNotFoundComponent,
});

function AdminLayout() {
  // Nothing while a sign-out is between clearing the session and reaching `/login` (D-4041).
  const signedIn = useHasSession();
  if (!signedIn) return null;
  return (
    <AdminShell>
      <Outlet />
    </AdminShell>
  );
}

/** SCR-SYS-500, inside the platform shell. */
function AdminErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation();
  const problem = toProblemDetails(error);
  return (
    <SystemState
      icon="exclamation-triangle"
      title={t('errors.title')}
      body={problem.detail ?? t('errors.generic')}
      correlationId={problem.correlationId}
      actions={
        <button type="button" className="btn btn-primary" onClick={reset}>
          {t('common.retry')}
        </button>
      }
    />
  );
}

/** SCR-SYS-404, inside the platform shell — the way back is „Conturi", not the dashboard. */
function AdminNotFoundComponent() {
  const { t } = useTranslation();
  return (
    <SystemState
      icon="question-mark-circle"
      title={t('errors.notFoundTitle')}
      body={t('errors.notFoundBody')}
      actions={
        <Link to="/admin/accounts" className="btn btn-primary">
          {t('admin.nav.accounts')}
        </Link>
      }
    />
  );
}
